import { getSoundEnabled, setSoundEnabled as storeSoundEnabled } from '@/utils/storage';
import { MUSIC, SFX, type MusicKey, type SfxKey, type SoundDef } from './soundConfig';

type PoolItem = {
  def: SoundDef;
  items: HTMLAudioElement[];
  idx: number;
};

export class SoundManager {
  private static instance: SoundManager | null = null;
  private readonly forceMuted = false;
  private readonly lowPerformanceMode = SoundManager.isLikelyMobileOrLowPerformanceDevice();
  private enabled = getSoundEnabled();
  private sfxPools: Map<SfxKey, PoolItem> = new Map();
  private music: Map<MusicKey, HTMLAudioElement> = new Map();
  private lastPlayAt: Map<SfxKey, number> = new Map();
  private readonly recentSfxPlayTimes: number[] = [];
  private readonly maxSfxPerSecond = this.lowPerformanceMode ? 2 : 40;
  private readonly lowPerformanceAllowedSfx: ReadonlySet<SfxKey> = new Set([
    'ui_click',
    'bet_change',
    'fruit_cut',
    'skull_hit',
    'gray_hit',
    'bonus_win',
    'instant_cashout',
    'cashout',
    'turbo_on',
    'turbo_off',
  ]);
  private readonly highPrioritySfx: ReadonlySet<SfxKey> = new Set([
    'ui_click',
    'bet_change',
  ]);
  private minIntervalMs: Partial<Record<SfxKey, number>> = {
    spin_start: this.lowPerformanceMode ? 1600 : 1200,
    fruit_cut: this.lowPerformanceMode ? 1800 : 1200,
    sector_fill: this.lowPerformanceMode ? 450 : 0,
    sector_drain: this.lowPerformanceMode ? 450 : 0,
    reel_stop: this.lowPerformanceMode ? 650 : 0,
    spin_stop: this.lowPerformanceMode ? 650 : 0,
    juice_splash: this.lowPerformanceMode ? 700 : 0,
  };

  static get(): SoundManager {
    if (!this.instance) this.instance = new SoundManager();
    return this.instance;
  }

  static isLikelyMobileOrLowPerformanceDevice(): boolean {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent ?? '';
    const touchPoints = navigator.maxTouchPoints > 0;
    const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 0;
    const cores = navigator.hardwareConcurrency ?? 0;
    const coarsePointer = typeof window.matchMedia === 'function'
      ? window.matchMedia('(pointer: coarse)').matches
      : false;
    const isMobileUa = /Android|iPhone|iPad|iPod|Mobile|Opera Mini|IEMobile/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    const lowMemory = memory > 0 && memory <= 4;
    const lowCpu = cores > 0 && cores <= 6;
    if (isMobileUa || (coarsePointer && touchPoints)) return true;
    return (isAndroid && touchPoints) || (coarsePointer && touchPoints && (lowMemory || lowCpu));
  }

  init(): void {
    if (this.forceMuted) {
      this.enabled = false;
      storeSoundEnabled(false);
      return;
    }
    for (const key of Object.keys(SFX) as SfxKey[]) {
      const def = SFX[key];
      const concurrency = this.lowPerformanceMode ? 1 : (def.concurrency ?? 2);
      const items: HTMLAudioElement[] = [];
      for (let i = 0; i < concurrency; i++) {
        const audio = new Audio(def.src);
        audio.preload = this.lowPerformanceMode ? 'none' : 'auto';
        audio.volume = def.volume;
        items.push(audio);
      }
      this.sfxPools.set(key, { def, items, idx: 0 });
    }
    for (const key of Object.keys(MUSIC) as MusicKey[]) {
      const def = MUSIC[key];
      const audio = new Audio(def.src);
      audio.preload = this.lowPerformanceMode ? 'metadata' : 'auto';
      audio.loop = true;
      audio.volume = def.volume;
      this.music.set(key, audio);
    }
  }

  setEnabled(enabled: boolean): void {
    if (this.forceMuted) {
      this.enabled = false;
      storeSoundEnabled(false);
      return;
    }
    this.enabled = enabled;
    storeSoundEnabled(enabled);
    if (!enabled) {
      for (const pool of this.sfxPools.values()) {
        for (const audio of pool.items) {
          audio.pause();
          audio.currentTime = 0;
        }
      }
      for (const audio of this.music.values()) {
        audio.pause();
      }
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  isForcedMuted(): boolean {
    return this.forceMuted;
  }

  playSfx(key: SfxKey, volumeMul = 1): void {
    if (!this.enabled) return;
    if (typeof document !== 'undefined' && document.hidden) return;
    if (this.lowPerformanceMode && !this.lowPerformanceAllowedSfx.has(key)) return;
    const isHighPriority = this.highPrioritySfx.has(key);
    const now = performance.now();
    if (!isHighPriority) {
      this.cleanupSfxBudget(now);
      if (this.recentSfxPlayTimes.length >= this.maxSfxPerSecond) return;
    }
    if (this.lowPerformanceMode && this.isLowPrioritySfx(key)) return;
    const minInterval = this.minIntervalMs[key];
    const last = this.lastPlayAt.get(key) ?? -Infinity;
    if (minInterval && now - last < minInterval) return;
    this.lastPlayAt.set(key, now);
    const pool = this.sfxPools.get(key);
    if (!pool) return;
    let audio = pool.items[pool.idx];
    pool.idx = (pool.idx + 1) % pool.items.length;
    if (!audio.paused && !audio.ended) {
      const freeAudio = pool.items.find((item) => item.paused || item.ended);
      if (!freeAudio) return;
      audio = freeAudio;
    }
    const variance = this.lowPerformanceMode ? 0 : (pool.def.rateVariance ?? 0);
    const rate = variance > 0 ? 1 + (Math.random() * 2 - 1) * variance : 1;
    try {
      audio.currentTime = 0;
      if (audio.playbackRate !== rate) audio.playbackRate = rate;
      audio.volume = Math.min(1, pool.def.volume * volumeMul);
      if (!isHighPriority) this.recentSfxPlayTimes.push(now);
      void audio.play();
    } catch {
      // ignore playback errors (autoplay policy / missing file)
    }
  }

  playMusic(key: MusicKey): void {
    if (!this.enabled) return;
    const audio = this.music.get(key);
    if (!audio) return;
    try {
      audio.volume = MUSIC[key].volume;
      void audio.play();
    } catch {
      // ignore
    }
  }

  stopMusic(key: MusicKey): void {
    const audio = this.music.get(key);
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
  }

  private cleanupSfxBudget(now: number): void {
    const timeWindowMs = 1000;
    while (this.recentSfxPlayTimes.length > 0 && now - this.recentSfxPlayTimes[0] > timeWindowMs) {
      this.recentSfxPlayTimes.shift();
    }
  }

  private isLowPrioritySfx(key: SfxKey): boolean {
    return key === 'sector_fill' || key === 'sector_drain' || key === 'juice_splash';
  }
}
